<template>
  <button
    type="button"
    class="kds-small-profile-card"
    aria-label="Open account settings"
    @click="$emit('settings-click')"
  >
    <div
      class="kds-small-profile-card__avatar"
      aria-hidden="true"
    >
      <span class="kds-small-profile-card__initials">{{ initials }}</span>
    </div>
    <div class="kds-small-profile-card__text">
      <span class="kds-small-profile-card__name">{{ displayName }}</span>
      <span
        v-if="role"
        class="kds-small-profile-card__role"
      >{{ role }}</span>
    </div>
    <!-- Settings icon kept for visual appearance only — interaction handled by the wrapper button -->
    <span
      class="kds-small-profile-card__settings"
      aria-hidden="true"
      tabindex="-1"
    >
      <KdsIcon name="settings-2" :size="16" />
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';

interface Props {
  name: string;
  role: string;
  initials: string;
}

const props = defineProps<Props>();

defineEmits<{
  (e: 'settings-click'): void;
}>();

const displayName = computed(() => props.name || '—');
</script>

<style scoped lang="scss">
.kds-small-profile-card {
  // Reset native button styles
  appearance: none;
  border: none;
  border-radius: 0;
  text-align: left;
  width: 100%;

  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-top: var(--border);
  background: var(--paper);
  flex-shrink: 0;
  cursor: pointer;
  transition: background-color 120ms var(--ease-out);

  &:hover {
    background: var(--paper-3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: var(--r-pill);
    background: var(--ink);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__initials {
    font-family: var(--font-mono);
    font-weight: var(--fw-semibold);
    font-size: 13px;
    color: var(--paper);
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: var(--fs-body);
    font-weight: var(--fw-medium);
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__role {
    font-size: var(--fs-body-sm);
    font-family: var(--font-mono);
    color: var(--stone);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__settings {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-1);
    color: var(--stone);
    border-radius: var(--r-2);
    flex-shrink: 0;
    // Pointer-events none: interaction is on the wrapper button
    pointer-events: none;
  }
}
</style>
